import { ChevronDown, Info, Maximize2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Widget — lightweight card container for consultation data sections */
/* ------------------------------------------------------------------ */

interface WidgetProps {
  children: React.ReactNode;
  className?: string;
  /** Allow the widget to grow to fill remaining space */
  grow?: boolean;
}

export const Widget = ({ children, className, grow }: WidgetProps) => (
  <div
    className={cn(
      "rounded-2xl border border-border/60 bg-card shadow-sm",
      grow && "flex-1 overflow-hidden flex flex-col min-h-0",
      className
    )}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  WidgetHeader                                                       */
/* ------------------------------------------------------------------ */

interface WidgetHeaderProps {
  title: string;
  onAdd?: () => void;
  onInfo?: () => void;
  onMaximize?: () => void;
  hasInfo?: boolean;
  className?: string;
  /** Optional content rendered between the title row and the action buttons. */
  extras?: React.ReactNode;
  /** Optional content rendered inline next to the title (left side). */
  titleAccessory?: React.ReactNode;
  /** Render the header as a collapse trigger with a rotating chevron. */
  collapsible?: boolean;
  /** Required when `collapsible` is true. */
  expanded?: boolean;
  /** Required when `collapsible` is true. */
  onToggle?: () => void;
}

export const WidgetHeader = ({
  title,
  onAdd,
  onInfo,
  onMaximize,
  hasInfo = false,
  className,
  extras,
  titleAccessory,
  collapsible = false,
  expanded = false,
  onToggle,
}: WidgetHeaderProps) => {
  const innerContent = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        {title && (
          <span className="z-body-md text-foreground truncate">{title}</span>
        )}
        {titleAccessory}
        {hasInfo && onInfo && (
          <button
            type="button"
            className="flex items-center justify-center rounded-full p-0.5 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onInfo();
            }}
          >
            <Info className="size-3" />
          </button>
        )}
        {hasInfo && !onInfo && (
          <span className="flex items-center justify-center rounded-full p-0.5 text-muted-foreground/25 cursor-default">
            <Info className="size-3" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {extras}
        {onMaximize && (
          <button
            type="button"
            className="flex items-center justify-center size-5 rounded-md bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onMaximize();
            }}
          >
            <Maximize2 className="size-3" />
          </button>
        )}
        {onAdd && (
          <button
            type="button"
            className="flex items-center justify-center size-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
          >
            <Plus className="size-3" />
          </button>
        )}
        {collapsible && (
          <ChevronDown
            className={cn(
              "size-4 text-muted-foreground transition-transform",
              expanded && "rotate-180"
            )}
          />
        )}
      </div>
    </>
  );

  if (collapsible) {
    // Use a div with role=button so child action buttons (Edit, Add, etc.)
    // remain valid HTML (button-in-button is invalid).
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle?.();
          }
        }}
        aria-expanded={expanded}
        className={cn(
          "flex w-full items-center justify-between gap-3 px-3 py-1.5 cursor-pointer hover:bg-muted/30 transition-colors text-left select-none",
          className
        )}
      >
        {innerContent}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-3 py-1.5",
        className
      )}
    >
      {innerContent}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  WidgetContent                                                      */
/* ------------------------------------------------------------------ */

interface WidgetContentProps {
  children: React.ReactNode;
  className?: string;
}

export const WidgetContent = ({ children, className }: WidgetContentProps) => (
  <div className={cn("px-3 pb-3", className)}>{children}</div>
);

/* ------------------------------------------------------------------ */
/*  SubsectionHeader (for grouped content within a widget)             */
/* ------------------------------------------------------------------ */

interface SubsectionHeaderProps {
  title: string;
  onAdd?: () => void;
  className?: string;
}

export const SubsectionHeader = ({
  title,
  onAdd,
  className,
}: SubsectionHeaderProps) => (
  <div className={cn("flex items-center justify-between mb-1.5", className)}>
    <span className="z-eyebrow text-muted-foreground">{title}</span>
    {onAdd && (
      <button
        type="button"
        className="flex items-center justify-center size-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        onClick={onAdd}
      >
        <Plus className="size-3" />
      </button>
    )}
  </div>
);
